import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiX, FiInfo, FiCalendar } from 'react-icons/fi';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getYesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function ManagerAddLeaveModal({
  isOpen,
  onClose,
  subordinates = [],
  preSelectedEmp = null,
  onSuccess,
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [leaveType, setLeaveType] = useState('CASUAL');
  const [isLop, setIsLop] = useState(false);
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDaySession, setHalfDaySession] = useState('FIRST_HALF');
  const [startDate, setStartDate] = useState(getTodayStr());
  const [endDate, setEndDate] = useState(getTodayStr());
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (preSelectedEmp) {
      const empId = typeof preSelectedEmp === 'string' ? preSelectedEmp : preSelectedEmp.employeeId;
      setEmployeeId(empId || '');
    } else if (subordinates.length > 0 && !employeeId) {
      setEmployeeId(subordinates[0].employeeId || '');
    }
    setStartDate(getTodayStr());
    setEndDate(getTodayStr());
    setReason('');
    setLeaveType('CASUAL');
    setIsLop(false);
    setIsHalfDay(false);
    setHalfDaySession('FIRST_HALF');
  }, [isOpen, preSelectedEmp, subordinates, employeeId]);

  const selectedSub = useMemo(() => {
    return subordinates.find(s => s.employeeId === employeeId);
  }, [subordinates, employeeId]);

  const daysCount = useMemo(() => {
    if (isHalfDay) return 0.5;
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;
    return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [startDate, endDate, isHalfDay]);

  const setPresetDates = (preset) => {
    const today = getTodayStr();
    const yest = getYesterdayStr();
    if (preset === 'TODAY') {
      setStartDate(today);
      setEndDate(today);
    } else if (preset === 'YESTERDAY') {
      setStartDate(yest);
      setEndDate(yest);
    } else if (preset === 'PAST_2') {
      setStartDate(yest);
      setEndDate(today);
      if (isHalfDay) setIsHalfDay(false);
    }
  };

  const handleStartDateChange = (val) => {
    setStartDate(val);
    if (isHalfDay) {
      setEndDate(val);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId) {
      toast.error('Please select a subordinate');
      return;
    }
    if (isHalfDay && startDate !== endDate) {
      toast.error('Half-day leave must start and end on the same day');
      return;
    }
    if (daysCount <= 0) {
      toast.error('Start date must be before or equal to end date');
      return;
    }

    try {
      setSubmitting(true);
      const toDateArray = (str) => str.split('-').map(Number);
      const payload = {
        employeeId,
        leaveType: isLop ? 'UNPAID' : leaveType,
        startDate: toDateArray(startDate),
        endDate: toDateArray(endDate),
        reason: reason.trim() || 'Recorded by Manager',
        isLop,
        isHalfDay,
        halfDaySession: isHalfDay ? halfDaySession : null,
      };

      const res = await api.post('/leave/manager/add', payload);
      const data = res.data?.data || res.data;
      toast.success(
        `Leave for ${selectedSub?.fullName || employeeId} recorded and approved successfully!`
      );
      if (onSuccess) onSuccess(data);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || err.message || 'Failed to add leave';
      toast.error(typeof msg === 'string' ? msg : 'Error adding leave');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const drawerNode = (
    <div className="fixed inset-0 z-[99999] overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-xs transition-opacity duration-200"
        onClick={onClose}
      />

      {/* Slide-over sidebar panel from the right */}
      <div className="fixed inset-y-0 right-0 max-w-full flex pl-0 sm:pl-8">
        <div className="w-screen max-w-lg bg-white shadow-2xl flex flex-col transform transition-transform duration-200 ease-out border-l border-gray-200">
          
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between shrink-0 bg-white">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Add Subordinate Leave</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Record an approved leave for your direct report
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              title="Close panel"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Form Body with generous spacing */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6 text-xs">
            
            {/* 1. Employee Selection */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Direct Report <span className="text-rose-500">*</span>
              </label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-900 focus:outline-none focus:border-[#008cd6] transition-colors"
              >
                {subordinates.length === 0 ? (
                  <option value="">No direct reports found</option>
                ) : (
                  subordinates.map(sub => (
                    <option key={sub.id || sub.employeeId} value={sub.employeeId}>
                      {sub.fullName || sub.username} (#{sub.employeeId}) &mdash; {sub.department || sub.jobTitle || 'Team'}
                    </option>
                  ))
                )}
              </select>
              {selectedSub && (
                <div className="pt-1 text-[11px] text-gray-500 flex items-center gap-1.5">
                  <span className="font-semibold text-gray-800">{selectedSub.fullName || selectedSub.username}</span>
                  <span>&bull;</span>
                  <span className="font-mono text-gray-500">#{selectedSub.employeeId}</span>
                  <span>&bull;</span>
                  <span>{selectedSub.jobTitle || 'Employee'} {selectedSub.department && `(${selectedSub.department})`}</span>
                </div>
              )}
            </div>

            {/* 2. Leave Type & LOP */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Leave Type <span className="text-rose-500">*</span>
                </label>
                <select
                  value={isLop ? 'UNPAID' : leaveType}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'UNPAID') {
                      setIsLop(true);
                      setLeaveType('UNPAID');
                    } else {
                      setIsLop(false);
                      setLeaveType(val);
                    }
                  }}
                  disabled={isLop}
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-900 focus:outline-none focus:border-[#008cd6] transition-colors disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="CASUAL">Casual Leave (Paid)</option>
                  <option value="SICK">Sick Leave (Paid)</option>
                  <option value="ANNUAL">Annual Leave (Paid)</option>
                  <option value="UNPAID">Loss of Pay (Unpaid / LOP)</option>
                </select>
              </div>

              {/* LOP Checkbox Toggle */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/80 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={isLop}
                  onChange={(e) => {
                    setIsLop(e.target.checked);
                    if (e.target.checked) setLeaveType('UNPAID');
                    else if (leaveType === 'UNPAID') setLeaveType('CASUAL');
                  }}
                  className="mt-0.5 rounded border-gray-300 text-[#008cd6] focus:ring-0 w-4 h-4 cursor-pointer"
                />
                <div>
                  <span className="text-xs font-semibold text-gray-800 block">Mark as Loss of Pay (LOP)</span>
                  <span className="text-[11px] text-gray-500">Record as unpaid leave without consuming paid leave balance</span>
                </div>
              </label>

              {/* Half-Day Checkbox Toggle */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50/80 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={isHalfDay}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsHalfDay(checked);
                    if (checked) {
                      setEndDate(startDate);
                    }
                  }}
                  className="mt-0.5 rounded border-gray-300 text-[#008cd6] focus:ring-0 w-4 h-4 cursor-pointer"
                />
                <div>
                  <span className="text-xs font-semibold text-gray-800 block">Record as Half-Day Leave</span>
                  <span className="text-[11px] text-gray-500">Duration will be 0.5 day for a single date session</span>
                </div>
              </label>

              {/* Half-Day Session Selector */}
              {isHalfDay && (
                <div className="flex items-center gap-3 p-2.5 rounded-lg border border-sky-200 bg-sky-50/50">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider shrink-0">
                    Session <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={halfDaySession}
                    onChange={(e) => setHalfDaySession(e.target.value)}
                    className="flex-1 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#008cd6]"
                  >
                    <option value="FIRST_HALF">First Half (Morning)</option>
                    <option value="SECOND_HALF">Second Half (Afternoon)</option>
                  </select>
                </div>
              )}
            </div>

            {/* 3. Date Range */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                  <FiCalendar size={13} className="text-[#008cd6]" />
                  <span>Leave Dates</span>
                  <span className="text-rose-500">*</span>
                </label>
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <span className="text-gray-400">Presets:</span>
                  <button
                    type="button"
                    onClick={() => setPresetDates('TODAY')}
                    className="text-[#008cd6] hover:underline cursor-pointer"
                  >
                    Today
                  </button>
                  <span className="text-gray-300">&bull;</span>
                  <button
                    type="button"
                    onClick={() => setPresetDates('YESTERDAY')}
                    className="text-[#008cd6] hover:underline cursor-pointer"
                  >
                    Yesterday
                  </button>
                  <span className="text-gray-300">&bull;</span>
                  <button
                    type="button"
                    onClick={() => {
                      setIsHalfDay(true);
                      setEndDate(startDate);
                    }}
                    className={`cursor-pointer hover:underline ${isHalfDay ? 'font-bold text-[#008cd6]' : 'text-[#008cd6]'}`}
                  >
                    0.5 Day
                  </button>
                  {!isHalfDay && (
                    <>
                      <span className="text-gray-300">&bull;</span>
                      <button
                        type="button"
                        onClick={() => setPresetDates('PAST_2')}
                        className="text-[#008cd6] hover:underline cursor-pointer"
                      >
                        Past 2 Days
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="block text-[11px] text-gray-500 mb-1">Start Date</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    required
                    className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-[#008cd6]"
                  />
                </div>
                <div>
                  <span className="block text-[11px] text-gray-500 mb-1">End Date</span>
                  <input
                    type="date"
                    value={isHalfDay ? startDate : endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={isHalfDay}
                    required
                    className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-[#008cd6] disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-1 text-[11px]">
                <span className="text-gray-500">Calculated Duration:</span>
                <span className={daysCount > 0 ? 'font-semibold text-gray-900' : 'font-semibold text-rose-600'}>
                  {isHalfDay ? `0.5 day (${halfDaySession === 'SECOND_HALF' ? 'Second Half' : 'First Half'})` : daysCount > 0 ? `${daysCount} day${daysCount > 1 ? 's' : ''}` : 'Start date must be before or equal to end date'}
                </span>
              </div>
            </div>

            {/* 4. Reason */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Reason / Remarks
              </label>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Approved medical leave or personal matter recorded on behalf of employee..."
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-[#008cd6] leading-relaxed"
              />
            </div>

            {/* 5. Clean info banner */}
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-sky-50/70 border border-sky-100 text-[11px] text-sky-900 leading-relaxed">
              <FiInfo size={14} className="text-[#008cd6] shrink-0 mt-0.5" />
              <span>
                <strong>Merged Approval:</strong> Leaves added by a manager are immediately recorded as approved and deducted from the subordinate's leave balance.
              </span>
            </div>

          </form>

          {/* Fixed Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/80 flex items-center justify-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-200/70 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || daysCount === 0 || !employeeId}
              className="px-5 py-2 rounded-lg text-xs font-semibold text-white bg-[#008cd6] hover:bg-[#0070b0] transition-colors disabled:opacity-50 cursor-pointer shadow-xs"
            >
              {submitting ? 'Recording Leave...' : 'Save & Approve Leave'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(drawerNode, document.body) : drawerNode;
}
